import {
  AlertCircle,
  AlertTriangle,
  Bell,
  Building2,
  CalendarClock,
  CalendarX,
  CheckCheck,
  CheckCircle,
  Lock,
  MessageCircle,
  Phone,
  ShieldCheck,
  UserCheck,
  UserCog,
  UserPlus,
  UserX,
  type LucideIcon,
} from 'lucide-react'

import type { NavigateOptions } from '@tanstack/react-router'

import type { Notification, NotificationType } from '@/shared/api/notificationsApi'

// Central per-type config: icon + tint + deep-link target.
// Single source of truth — used by the bell dropdown, full feed, and any
// future surface (toast, email rendering, etc.). Mirrors the matrix in
// MOBILE_API_SPEC §6 and the FE notification tint map in
// docs/mock/mobile-design-tokens.json.

export interface NotificationVisual {
  icon: LucideIcon
  /** Background tint for the circular icon */
  bg: string
  /** Foreground color for the icon glyph */
  fg: string
}

const VISUAL: Record<NotificationType, NotificationVisual> = {
  // Issue lifecycle
  'issue.raised':              { icon: AlertCircle,    bg: 'bg-red-50',     fg: 'text-red-600' },
  'issue.resolved_by_hod':     { icon: CheckCircle,    bg: 'bg-blue-50',    fg: 'text-blue-600' },
  'issue.rejected_by_hod':     { icon: AlertTriangle,  bg: 'bg-amber-50',   fg: 'text-amber-700' },
  'issue.completed':           { icon: CheckCheck,     bg: 'bg-emerald-50', fg: 'text-emerald-600' },
  'issue.remark_added':        { icon: MessageCircle,  bg: 'bg-purple-50',  fg: 'text-purple-600' },
  'issue.closed_by_admin':     { icon: ShieldCheck,    bg: 'bg-blue-50',    fg: 'text-blue-600' },

  // Change-request workflow
  'change_request.submitted':  { icon: UserCog,        bg: 'bg-blue-50',    fg: 'text-blue-600' },
  'change_request.approved':   { icon: UserCheck,      bg: 'bg-emerald-50', fg: 'text-emerald-600' },
  'change_request.rejected':   { icon: UserX,          bg: 'bg-red-50',     fg: 'text-red-600' },

  // Assignment lifecycle
  'department.assigned':       { icon: Building2,      bg: 'bg-blue-50',    fg: 'text-blue-600' },
  'department.unassigned':     { icon: Building2,      bg: 'bg-slate-100',  fg: 'text-slate-500' },
  'task.assigned':             { icon: CalendarClock,  bg: 'bg-blue-50',    fg: 'text-blue-600' },
  'task.missed':               { icon: CalendarX,      bg: 'bg-red-50',     fg: 'text-red-600' },

  // Account state
  'account.reactivated':       { icon: UserPlus,       bg: 'bg-emerald-50', fg: 'text-emerald-600' },
  'account.password_reset':    { icon: Lock,           bg: 'bg-amber-50',   fg: 'text-amber-700' },
  'account.phone_changed_by_hod': { icon: Phone,       bg: 'bg-blue-50',    fg: 'text-blue-600' },
}

const FALLBACK_VISUAL: NotificationVisual = {
  icon: Bell,
  bg: 'bg-slate-100',
  fg: 'text-slate-500',
}

export function visualFor(type: NotificationType): NotificationVisual {
  return VISUAL[type] ?? FALLBACK_VISUAL
}

// ── Deep-link resolver ────────────────────────────────────────────────────
// Returns the TanStack Router NavigateOptions for the notification, or
// `null` if the notification is informational only (no destination on web).
// task.* events have no destination on web — they're FO-only on mobile.

export function deepLinkFor(notification: Notification): NavigateOptions | null {
  const { type, issue_id } = notification

  switch (type) {
    case 'issue.raised':
    case 'issue.resolved_by_hod':
    case 'issue.rejected_by_hod':
    case 'issue.completed':
    case 'issue.remark_added':
    case 'issue.closed_by_admin':
      return issue_id
        ? { to: '/issues/$issueId', params: { issueId: issue_id } }
        : null

    case 'change_request.submitted':
      // Admins are recipients — link to the queue page they can act on.
      return { to: '/user-management/change-requests' }

    case 'change_request.approved':
    case 'change_request.rejected':
      // Users see their own decision on profile.
      return { to: '/profile' }

    case 'department.assigned':
    case 'department.unassigned':
      // HODs land on their site directory (renamed from /hod/departments).
      return { to: '/hod/sites' }

    case 'task.assigned':
    case 'task.missed':
      // FO-only on mobile; admin/HOD on web get them as info-only.
      return null

    case 'account.reactivated':
    case 'account.password_reset':
      // Security info; dismiss only.
      return null

    case 'account.phone_changed_by_hod':
      // Admin audit event — informational. No deep-link target.
      return null

    default:
      return null
  }
}

// ── Relative-time formatter (for notification rows) ────────────────────────
// Cheap implementation — good enough for "12m ago" / "2h ago" / "3d ago".

export function timeAgo(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) {return ''}
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (diffSec < 60) {return 'just now'}
  if (diffSec < 3600) {return `${Math.floor(diffSec / 60)}m ago`}
  if (diffSec < 86400) {return `${Math.floor(diffSec / 3600)}h ago`}
  if (diffSec < 7 * 86400) {return `${Math.floor(diffSec / 86400)}d ago`}
  if (diffSec < 30 * 86400) {return `${Math.floor(diffSec / (7 * 86400))}w ago`}
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

// ── Cron-burst grouping helper ────────────────────────────────────────────
// Per MOBILE_API_SPEC §6.4: nightly scheduler can fire 12 task.assigned
// events back-to-back. Group them in the dropdown so the bell isn't 12 deep.

export function isCronBurst(n: Notification): boolean {
  if (n.type !== 'task.assigned') {return false}
  const md = n.metadata as { assigned_by?: string } | null
  return md?.assigned_by === 'scheduler'
}
